import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Asset, Hotspot, TourData } from "../../../Types";
import {
  UseAssetsQuery,
  UseDownloadAsset,
  UseSaveTour,
} from "../../../api/calls/Queries";
import { Play, Stop } from "../../Icons/Player";
import Headphones from "../../Icons/Headphones";
import { useForm } from "react-hook-form";
import FileSelectorButton from "../GuiElements/FileSelectorButton";
import OtherHotspots from "./OtherHotspots";
import {
  useAssetListStore,
  useCurrentSceneStore,
  useEditingElementStore,
} from "../../../api/Zustand";
import { EditPencil, Trashbin } from "../../Icons/gui-elements-icons";
import { getLanguageFile } from "../../common/GetLanguageFile";

const AudioPopupEditing = (props: { id: string; tourID: number }) => {
  const queryClient = useQueryClient();
  const { setEditingElem } = useEditingElementStore();

  const { setOpen } = useAssetListStore();

  const tourData: TourData | undefined = queryClient.getQueryData([
    "GetTour",
    props.tourID,
  ]);

  const { mutateAsync: saveTour, isPending: isSaving } = UseSaveTour();

  const { data: audioList } = UseAssetsQuery(props.tourID, 3);
  const [currentAudio, setCurrentAudio] = React.useState<Hotspot>();

  const { currentSceneID } = useCurrentSceneStore();

  useEffect(() => {
    if (tourData) {
      let temp = tourData?.scenes
        ?.find((one) => one.id === currentSceneID)
        ?.hotspots.find((one) => one.id === props.id);
      setCurrentAudio(temp);
    }
  }, [tourData, props.id, currentSceneID]);

  const currentAudioMemo = React.useMemo(() => {
    const temp = tourData?.scenes
      ?.find((one) => one.id === currentSceneID)
      ?.hotspots.find((one) => one.id === props.id);

    return temp;
  }, [props.id, currentSceneID, tourData]);

  const { handleSubmit, register, formState, setValue } = useForm<Hotspot>({
    values: currentAudioMemo,
  });

  React.useEffect(() => {
    if (currentAudio?.target) {
      setValue("target", currentAudio.target);
    }
  }, [currentAudio?.target, setValue]);

  const onSubmit = (data: Hotspot) => {
    if (currentAudio) {
      const dataToSend = {
        ...data,
        EDIT_POS: undefined,
        isNew: undefined,
        id: currentAudio.id,
        x: currentAudio.x,
        y: currentAudio.y,
        z: currentAudio.z,
      };

      const newTourData = queryClient.setQueryData(["GetTour", props.tourID], {
        ...tourData,
        scenes: [
          ...tourData!.scenes.map((sc) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  hotspots: sc.hotspots.map((hs: Hotspot) =>
                    hs.id === currentAudio.id ? dataToSend : hs
                  ),
                }
              : sc
          ),
        ],
      });
      saveTour(newTourData as TourData);
    }
  };

  const handlePosEdit = useCallback(async () => {
    if (currentAudio) {
      queryClient.setQueryData(["GetTour", props.tourID], {
        ...tourData,
        scenes: [
          ...tourData!.scenes.map((sc) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  hotspots: sc.hotspots.map((hs: Hotspot) =>
                    hs.id === currentAudio.id
                      ? {
                          ...currentAudio,
                          EDIT_POS: currentAudio.EDIT_POS ? undefined : true,
                        }
                      : hs
                  ),
                }
              : sc
          ),
        ],
      });
    }
  }, [currentAudio, currentSceneID, queryClient, tourData, props.tourID]);

  // const handleSave = useCallback(async () => {
  //   if (currentAudio) {
  //     const newTourData = queryClient.setQueryData(["GetTour", props.tourID], {
  //       ...tourData,
  //       scenes: [
  //         ...tourData!.scenes.map((sc) =>
  //           sc.id === currentSceneID
  //             ? {
  //                 ...sc,
  //                 hotspots: sc.hotspots.map((hs: Hotspot) =>
  //                   hs.id === currentAudio.id
  //                     ? {
  //                         ...currentAudio,
  //                         EDIT_POS: undefined,
  //                         isNew: undefined,
  //                       }
  //                     : hs
  //                 ),
  //               }
  //             : sc
  //         ),
  //       ],
  //     });
  //     saveTour(newTourData as TourData);
  //   }
  // }, [
  //   currentAudio,
  //   currentSceneID,
  //   queryClient,
  //   tourData,
  //   props.tourID,
  //   saveTour,
  // ]);

  const onTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (currentAudio) {
        queryClient.setQueryData(["GetTour", props.tourID], {
          ...tourData,
          scenes: [
            ...tourData!.scenes.map((sc) =>
              sc.id === currentSceneID
                ? {
                    ...sc,
                    hotspots: sc.hotspots.map((hs: Hotspot) =>
                      hs.id === currentAudio.id
                        ? {
                            ...currentAudio,
                            title: e.target.value,
                          }
                        : hs
                    ),
                  }
                : sc
            ),
          ],
        });
      }
    },
    [currentAudio, currentSceneID, queryClient, props.tourID, tourData]
  );

  const handleDelete = async () => {
    const tempSceneSpots = tourData!.scenes
      .find((one) => one.id === currentSceneID)!
      .hotspots.filter((spot) => spot.id !== currentAudio!.id);

    const newTourData = queryClient.setQueryData(["GetTour", props.tourID], {
      ...tourData,
      scenes: tourData!.scenes.map((scene) =>
        scene.id === currentSceneID
          ? { ...scene, hotspots: tempSceneSpots }
          : scene
      ),
    });

    setEditingElem("scene", currentSceneID!);

    saveTour(newTourData as TourData);
  };

  const displayedTitle: string | undefined = useMemo(() => {
    if (!currentAudio?.target) {
      return getLanguageFile("right_panel.no_audio_file");
    } else if (
      audioList?.find((one: Asset) => one.assetId === currentAudio.target)
        ?.assetDescription
    )
      return audioList?.find(
        (one: Asset) => one.assetId === currentAudio.target
      )?.assetDescription;
    else return getLanguageFile("right_panel.audio_name_error");
  }, [currentAudio, audioList]);

  if (currentAudio) {
    return (
      <div className="right-side-panel-editor">
        <h3>{getLanguageFile("right_panel.audio")}</h3>

        <form onSubmit={handleSubmit(onSubmit)} className="right-panel">
          <label>
            {getLanguageFile("right_panel.title")}
            <input
              type="text"
              {...register("title")}
              onBlur={(e) => onTitleChange(e)}
            />
          </label>
          <input
            disabled
            type="text"
            {...register("target", { required: true })}
            hidden
          />

          <button
            type="button"
            id="edit"
            className={currentAudio.EDIT_POS ? "active" : undefined}
            onClick={handlePosEdit}
            style={{}}
          >
            {currentAudio.EDIT_POS ? (
              getLanguageFile("right_panel.save_position")
            ) : (
              <>
                {getLanguageFile("right_panel.edit_position")} <EditPencil />
              </>
            )}
          </button>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{
                visibility: formState.errors.target ? "visible" : "hidden",
                fontWeight: 600,
              }}
            >
              {getLanguageFile("right_panel.no_audio_file")}
            </span>

            <label
              htmlFor="audio"
              style={{ display: "flex", flexDirection: "column" }}
            >
              {getLanguageFile("right_panel.audio_file")}
            </label>
            <FileSelectorButton id="audio" onClickFunction={() => setOpen(3)}>
              {displayedTitle ?? getLanguageFile("generics.error")}
            </FileSelectorButton>

            <div>
              {audioList ? (
                <AudioPlayerThingy
                  audioList={audioList}
                  currentAudio={currentAudio.target}
                />
              ) : (
                <i>{getLanguageFile("generics.loading")}</i>
              )}
            </div>
          </div>

          <div style={{ display: "flex" }}>
            <button
              type="submit"
              disabled={isSaving}
              id="save"
              style={{ flex: 1 }}
              // onClick={() => handleSave()}
            >
              {isSaving
                ? getLanguageFile("generics.saving")
                : currentAudio.isNew
                ? getLanguageFile("generics.create")
                : getLanguageFile("generics.save")}
            </button>

            <button
              type="button"
              style={{ flex: 1 }}
              id="delete"
              onClick={() => handleDelete()}
            >
              {currentAudio.isNew ? (
                getLanguageFile("generics.cancel")
              ) : (
                <>
                  {getLanguageFile("generics.delete")} <Trashbin />
                </>
              )}
            </button>
          </div>
        </form>

        <OtherHotspots
          currentHotspot={currentAudio}
          icon={<Headphones width="1em" height={"1em"} fill="#fff" />}
          tourData={tourData!}
          type="audio"
        ></OtherHotspots>
      </div>
    );
  }
};

export const AudioPlayerThingy = (props: {
  currentAudio: string | number | undefined;
  audioList: Asset[];
  small?: boolean;
}) => {
  const { data: audioBlob, isLoading: isDownloadingAudio } = UseDownloadAsset(
    props.audioList?.find((one: Asset) => one.assetId === props.currentAudio)
      ?.assetKeyFull
  );

  const audioRef = React.useRef<HTMLAudioElement>(null);
  const audioTest = React.useRef(new Audio());

  useEffect(() => {
    if (audioBlob) audioTest.current.src = audioBlob;
  }, [audioBlob]);

  const [percentage, setPercentage] = useState<number>(0);

  const [playing, setPlaying] = useState<boolean>(false);

  const [dur, setDur] = useState<number>(0);

  return (
    <div>
      {/* {audioBlob && (
        <button onClick={() => audioTest.current.play()}>
          {audioTest.current?.duration}
        </button>
      )} */}

      <label>
        {audioBlob && !isDownloadingAudio && (
          <button
            type="button"
            onClick={() => {
              if (playing) {
                if (audioRef.current) audioRef.current.currentTime = 0;
                audioRef.current?.pause();
              } else audioRef.current?.play();
            }}
            style={{
              position: "relative",
              overflow: "clip",
              width: props.small ? undefined : "100%",
              boxSizing: "border-box",
              minWidth: 0,
              margin: 0,
              marginTop: props.small ? 0 : 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              // paddingInline: 0,
              gap: 20,
            }}
          >
            {audioRef.current?.paused ? <Play /> : <Stop />}
            {!props.small && (
              <>
                <span style={{ userSelect: "none" }}>Anteprima</span>
                {secondsParser(dur)}s
              </>
            )}
            <div
              style={{
                height: "100%",
                width: `${percentage * 100}%`,
                background: "#fff4",
                position: "absolute",
                left: 0,
                top: 0,
              }}
            ></div>
          </button>
        )}
      </label>
      {audioBlob && (
        <audio
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            const dur = e.currentTarget.duration;

            if (dur < 10) {
              setPercentage(t / dur);
            } else setPercentage(t / 10);

            if (t > 10) {
              e.currentTarget.pause();
              e.currentTarget.currentTime = 0;
            }
          }}
          onEnded={(e) => {
            e.currentTarget.currentTime = 0;
            setPlaying(false);
          }}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onLoadedMetadata={(e) => {
            setDur(e.currentTarget.duration);
          }}
          ref={audioRef}
          src={audioBlob}
        />
      )}
    </div>
  );
};

const secondsParser: (s: number) => string = (s) => {
  const min = Math.floor(s / 60);
  const sec = s % 60;

  return min + ":" + Math.ceil(sec).toString().padStart(2, "0");
};

export default AudioPopupEditing;
